//@flow
import React from 'react';
import EditableCell from 'common/components/core/table/EditableCell';
import withHandlers from 'recompose/withHandlers';
import compose from 'recompose/compose';
import FixedDataTable from 'common/components/core/table/FixedDataTable';
import { dashboardColors } from 'common/enums/ColorEnum';
import { Column, Cell } from 'fixed-data-table-2';
import fixedDataTableTheme from './FixedDataTable.scss';
import classnames from 'classnames';
import errorIconTheme from 'common/components/core/icon/ErrorIcon.scss';
import Icon from 'common/components/core/icon';
import testId from 'common/tests/entity/entityIds.json';
import { DivLoader } from 'common/components/core/wrappers/Div';

type CellRenderProps = {
    columnKey: string,
    rowIndex: number,
    rowId: string,
    row: Object | any,
    header: Object | any,
    colIndex: number
};

const TableEditHandlers = compose(
    withHandlers({
        _getCellProps: ({ editedCells, onTableEdit, getCellProps }) => ({
            columnKey,
            rowIndex,
            rowId,
            row,
            header,
            colIndex,
            resetTableDefaultScroll
        }: CellRenderProps) => {
            let isEdited = false;
            const cellData = row.get(columnKey);
            const editedRecord =
                editedCells &&
                editedCells.find(
                    editedCell =>
                        editedCell.rowId === rowId &&
                        editedCell.headerId === columnKey
                );
            const editedData = editedRecord && editedRecord.value;
            if (editedRecord && editedData !== cellData) {
                isEdited = true;
            }
            const otherCellProps =
                getCellProps?.({
                    columnKey,
                    rowIndex,
                    rowId,
                    row,
                    header,
                    colIndex
                }) || {};

            const cellFocus = otherCellProps?.onFocus;
            otherCellProps.onFocus = (...args) => {
                resetTableDefaultScroll();
                cellFocus?.(...args);
            };
            return {
                isEdited,
                disabled: header.disabled,
                readOnly: header.readOnly,
                inputValue: isEdited ? editedData : cellData,
                onCellEdit: onTableEdit,
                rowIndex,
                colIndex,
                ...otherCellProps
            };
        }
    }),
    withHandlers({
        getRowDataAtIndex: ({ fetchPage }) => ({
            pagedResults,
            isSearching,
            rowIndex
        }) => {
            const { 1: pageOne } = pagedResults;
            if (!pageOne) {
                if (isSearching) {
                    return;
                }
                throw new Error('page 1 missing');
            }
            const pageNumber = Math.floor(rowIndex / 25) + 1;
            const page = pagedResults[pageNumber];
            if (!page) {
                fetchPage?.(pageNumber);
                return;
            }
            const paginatedRowIndex = rowIndex % 25;
            return {
                row: page.rows.get(paginatedRowIndex),
                pageNumber,
                paginatedRowIndex
            };
        }
    }),
    withHandlers({
        renderCells: ({
            tableData: { rows: pagedResults },
            _getCellProps,
            getRowDataAtIndex
        }) => ({
            rowIndex,
            columnKey,
            header,
            colIndex,
            resetTableDefaultScroll
        }: {
            columnKey: string,
            rowIndex: number,
            header: Object,
            colIndex: number
        }) => {
            const rowData = getRowDataAtIndex({
                pagedResults,
                rowIndex
            });
            if (!rowData) {
                return <DivLoader theme={fixedDataTableTheme} loading />;
            }
            const { row, pageNumber, paginatedRowIndex } = rowData;
            const rowId = row.get('id');
            const otherProps =
                _getCellProps?.({
                    columnKey,
                    rowIndex,
                    rowId,
                    row,
                    header,
                    colIndex,
                    resetTableDefaultScroll
                }) || {};

            const { cellData } = otherProps;
            const key = `${columnKey}.${rowIndex}`;
            return (
                <EditableCell
                    key={key}
                    onChangeMetaData={{
                        rowIndex,
                        headerId: columnKey,
                        rowId,
                        row,
                        header,
                        pageNumber,
                        paginatedRowIndex
                    }}
                    content={cellData}
                    theme={fixedDataTableTheme}
                    {...otherProps} // might need to override some props, hence spreading otherProps in the end
                />
            );
        }
    }),
    withHandlers({
        renderColumn: ({ showNonImportableColumns, renderCells }) => ({
            header,
            columnWidths,
            colIndex,
            resetTableDefaultScroll
        }) => {
            if (!showNonImportableColumns && !header.importable) {
                return null; // hide non importable columns
            }
            const headerCell = (
                <Cell className={fixedDataTableTheme.tableHeader}>
                    <span className={fixedDataTableTheme.text}>
                        {header.title}
                    </span>
                </Cell>
            );
            return (
                <Column
                    columnKey={header.id}
                    key={header.id}
                    isResizable
                    isReorderable={false}
                    allowCellsRecycling
                    header={headerCell}
                    // eslint-disable-next-line react/jsx-no-bind
                    cell={({ columnKey, rowIndex }) =>
                        renderCells({
                            columnKey,
                            rowIndex,
                            header,
                            colIndex,
                            resetTableDefaultScroll
                        })
                    }
                    width={columnWidths[header.id]}
                    fixed={header.fixed}
                />
            );
        }
    }),
    withHandlers({
        renderErrorStatus: ({
            theme,
            getEntityErrorCount,
            getRowDataAtIndex,
            tableData: { rows: pagedResults }
        }) => ({ rowIndex }: CellRenderProps) => {
            const rowData = getRowDataAtIndex({
                pagedResults,
                rowIndex
            });
            if (!rowData) {
                return <DivLoader theme={fixedDataTableTheme} loading />;
            }
            const { row } = rowData;
            const rowId = row.get('id');
            const errorCount = getEntityErrorCount({ rowId });
            const hasError = !!errorCount;
            return (
                <div
                    testId={`${testId.rowErrorCount}-${rowIndex}`}
                    className={classnames(theme.errorStatusCellWrapper, {
                        [theme.hasError]: hasError
                    })}
                >
                    <Icon
                        theme={hasError ? errorIconTheme : theme}
                        bgColor={hasError && dashboardColors.salmonTwo.name}
                        iconColor={
                            hasError
                                ? dashboardColors.white.name
                                : dashboardColors.coolGreen.name
                        }
                        className={
                            hasError ? errorIconTheme.errorIcon : theme.noErrors
                        }
                        icon={hasError ? 'alert' : 'circle'}
                    />
                    {hasError && errorCount && (
                        <div className={theme.rowErrorCount}>{errorCount}</div>
                    )}
                </div>
            );
        }
    }),
    withHandlers({
        renderErrorStatusHeader: () => () => (
            <Cell className={fixedDataTableTheme.tableHeader}>
                <div className={fixedDataTableTheme.text}>Status</div>
            </Cell>
        )
    }),
    withHandlers({
        renderExtraColumns: ({
            renderErrorStatus,
            renderErrorStatusHeader
        }) => () => (
            <Column
                columnKey="rowErrorStatus"
                cell={renderErrorStatus}
                header={renderErrorStatusHeader}
                width={54}
                fixed
            />
        )
    })
);

const EditableFixedDataTable = TableEditHandlers(FixedDataTable);
export { EditableFixedDataTable };
export default EditableFixedDataTable;
